import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Navigation state that asks the browse page to consider landing with the pill
 * bar at the top of the viewport, where it will pin.
 *
 * Carried as router state rather than inferred from the route, because the
 * same page is reached both ways: a reader who clicked Spaces is switching
 * views, and a reader who followed a link or a search result wants the page
 * from its beginning.
 */
export const SCROLL_TO_PILL_BAR_STATE = { scrollToPillBar: true } as const;

/** The sticky band this scrolls to, marked on the browse shell's own section. */
const PILL_BAR_SELECTOR = '[data-section="pill-bar"]';

/**
 * Treat the bar as pinned once the page is within a pixel of its landing.
 *
 * At exactly the landing the bar is already against the top of the viewport —
 * pinned, not merely in view — so that has to count as floating: the reader is
 * looking at a grid under a docked bar, and a switch should leave it docked.
 * Reading the boundary the other way left them at the top of the page instead,
 * because nothing then undid the browser's clamp.
 *
 * The epsilon is there because both sides are fractional. Without it a bar
 * sitting exactly on the line came out differently from one run to the next.
 */
const DOCK_EPSILON = 1;

/**
 * How long to keep correcting the landing, in ms.
 *
 * The incoming view lays out before its images have sized, so the document
 * grows under the landing and the browser clamps the scroll short. Rather than
 * writing a scroll on every frame for half a second, the landing is reapplied
 * only when the document actually changes size — which is the only thing that
 * can invalidate it — and gives up after this.
 */
const SETTLE_MS = 600;

/**
 * Whether the bar was floating when the reader clicked a view pill.
 *
 * Module scope, not a ref: the three views are three routes, so the browse page
 * unmounts and a fresh one mounts between the click and the navigation's
 * effect. A ref is recreated by that remount and the answer is lost.
 */
let wasFloatingOnClick = false;

/**
 * The band's own box, or the row inside it when the band has none.
 *
 * Above `md` the band is `display: contents` — deliberately, so the pill row
 * inside it sticks against the page column rather than against the band's own
 * two rows (see `DirectoryBrowsePage`). An element with `display: contents`
 * generates no box at all, so measuring it reads nothing. Measured on the
 * browse page, the band's first element child there is that sticky row, which
 * is the thing worth landing at the top.
 *
 * Below `md` the band is a real sticky box and is its own answer, which is
 * what carries its `scroll-margin-top` past the header.
 */
const scrollTargetIn = (band: Element): Element | null =>
    band.getClientRects().length > 0 ? band : band.firstElementChild;

/** The pill bar's row, or null when the page has no pill bar on screen. */
const findPillBarRow = (): HTMLElement | null => {
    const band = document.querySelector(PILL_BAR_SELECTOR);
    const target = band && scrollTargetIn(band);
    return target instanceof HTMLElement ? target : null;
};

/**
 * Where the page would have to scroll to put the bar at the top.
 *
 * A stuck sticky element measures at its pinned position, which says nothing
 * about where it belongs in the page — so it is unstuck for the length of one
 * read. This runs inside a frame, before paint, so nothing flashes.
 */
const landingFor = (row: HTMLElement): number => {
    const inlinePosition = row.style.position;
    row.style.position = 'static';
    const staticTop = window.scrollY + row.getBoundingClientRect().top;
    row.style.position = inlinePosition;
    const scrollMargin =
        Number.parseFloat(getComputedStyle(row).scrollMarginTop) || 0;
    return Math.max(0, staticTop - scrollMargin);
};

/**
 * Land a view switch where the reader can still see what they asked for.
 *
 * Two cases, decided by whether the pill bar is floating — pinned to the top of
 * the viewport because the reader has scrolled past where it actually sits:
 *
 * - **Not floating.** The bar is in view at its own place in the page, so the
 *   top of the grid is on screen already. Nothing moves; the reader keeps their
 *   position and the new grid replaces the old one under the bar.
 * - **Floating.** The reader is down inside the old grid. Keeping their offset
 *   would drop them into the middle of a listing they have never seen, with the
 *   start of it above the fold, so the bar comes to the top and the new grid
 *   opens under it.
 *
 * Which case it is has to be read on the click, not after the navigation: the
 * incoming view has not laid out yet when the effect runs, so the bar measures
 * against a document that is briefly the wrong height. The links pass
 * `preventScrollReset`, so doing nothing really does leave the reader where
 * they were.
 *
 * Only for a switch the reader made: Back and Forward restore their own
 * position, and a plain visit to the page starts at the top.
 */
export const useScrollToPillBar = (): void => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const requested =
        (location.state as { scrollToPillBar?: boolean } | null)
            ?.scrollToPillBar === true;

    useEffect(() => {
        const capture = (event: Event) => {
            const target = event.target;
            if (
                !(target instanceof Element) ||
                !target.closest(PILL_BAR_SELECTOR)
            ) {
                return;
            }
            const row = findPillBarRow();
            wasFloatingOnClick = row
                ? window.scrollY >= landingFor(row) - DOCK_EPSILON
                : false;
        };
        // Capture phase: the link's own handler navigates, so a bubbling
        // listener would read the position after the fact.
        document.addEventListener('click', capture, true);
        return () => document.removeEventListener('click', capture, true);
    }, []);

    useEffect(() => {
        /*
         * The decision is read here but not spent.
         *
         * One click can produce more than one navigation — the router follows
         * the pill's PUSH with a REPLACE of its own — and zeroing it on the
         * first left the second with nothing to act on. No landing was made,
         * and because the incoming view is briefly short enough for the browser
         * to clamp the scroll, the reader ended up at the top of the page
         * instead of where they had been. That is the whole of the
         * intermittency: it only appeared when the second navigation arrived
         * late enough to be a separate effect run.
         *
         * Holding it costs nothing. Every pill click writes the answer afresh,
         * and `requested` keeps this to view switches — so the only navigation
         * that can read a decision it did not make is the follow-up to the one
         * that did, which wants the same answer anyway.
         */
        if (!requested || navigationType === 'POP') {
            // Back and Forward restore their own position and an ordinary visit
            // starts at the top; either way the decision no longer applies.
            wasFloatingOnClick = false;
            return;
        }
        if (!wasFloatingOnClick) return;

        let observer: ResizeObserver | null = null;
        let frame = 0;
        let timer = 0;
        const stop = () => {
            observer?.disconnect();
            observer = null;
            if (frame) cancelAnimationFrame(frame);
            frame = 0;
            if (timer) window.clearTimeout(timer);
            timer = 0;
            window.removeEventListener('wheel', stop);
            window.removeEventListener('touchstart', stop);
            window.removeEventListener('keydown', stop);
            window.removeEventListener('pointerdown', stop);
        };

        const land = () => {
            const row = findPillBarRow();
            if (!row) return;
            const landing = landingFor(row);
            // Instant, not smooth: the listing under it has already been
            // replaced, so animating across it travels through a page that is
            // no longer the one the reader was looking at.
            if (Math.round(window.scrollY) !== Math.round(landing)) {
                window.scrollTo({ top: landing, behavior: 'instant' });
            }
        };

        // After the frame the router's own scroll handling writes in, so this
        // is the last word on where the page lands rather than a scroll it
        // undoes.
        frame = requestAnimationFrame(() => {
            frame = 0;
            land();
            // Only the document changing height can invalidate that landing,
            // so that is the only thing worth re-running it for.
            //
            // `body`, not `documentElement`: the root element's box is the
            // viewport and does not grow as the incoming grid's images size,
            // which is the growth this is here to follow.
            observer = new ResizeObserver(land);
            observer.observe(document.body);
            timer = window.setTimeout(stop, SETTLE_MS);
        });

        // Gives way the moment the reader takes over, so it can never fight
        // someone scrolling. Dragging the scrollbar raises neither wheel nor
        // touch, which is why pointerdown is here too.
        window.addEventListener('wheel', stop, { passive: true, once: true });
        window.addEventListener('touchstart', stop, {
            passive: true,
            once: true,
        });
        window.addEventListener('keydown', stop, { once: true });
        window.addEventListener('pointerdown', stop, {
            passive: true,
            once: true,
        });

        return stop;
        // `location.key` changes on every navigation, including one to the
        // view already showing.
    }, [requested, navigationType, location.key]);
};
